/**
 * Push Notification Permission & FCM Token Handler
 * Manages notification permissions and saves FCM tokens to Firestore
 */

import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { app, db } from '../firebaseConfig';
import { notifyBell } from '../utils/notifyBell';

// FCM VAPID Key - you'll need to generate this in Firebase Console
// Go to: Project Settings > Cloud Messaging > Web Push certificates
const VAPID_KEY = 'BKKLlnK5Wi_DUGL7_AkWIP5RTSiAg-uPSlWE7qvLtfneugNbWJrxayWqKpzgis4nUanXkwW9YX41Dpu0H2FkkjI';

// Storage key for permission state
const NOTIFICATION_ASKED_KEY = 'notification_permission_asked';
const NOTIFICATION_TOKEN_KEY = 'fcm_token';

/**
 * Check if notifications are supported
 */
export function isNotificationSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission() {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

/**
 * Check if we've already asked for permission
 */
export function hasAskedForPermission() {
  return localStorage.getItem(NOTIFICATION_ASKED_KEY) === 'true';
}

/**
 * Request notification permission
 * @returns {Promise<'granted'|'denied'|'default'>}
 */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    console.warn('[Notifications] Not supported in this browser');
    return 'unsupported';
  }

  localStorage.setItem(NOTIFICATION_ASKED_KEY, 'true');

  try {
    const permission = await Notification.requestPermission();
    console.log('[Notifications] Permission:', permission);
    return permission;
  } catch (error) {
    console.error('[Notifications] Permission request failed:', error);
    return 'denied';
  }
}

/**
 * Get device info for token storage
 */
function getDeviceInfo() {
  const ua = navigator.userAgent;
  let device = 'unknown';
  let browser = 'unknown';

  // Detect device
  if (/iPhone|iPad|iPod/.test(ua)) {
    device = 'iOS';
  } else if (/Android/.test(ua)) {
    device = 'Android';
  } else if (/Windows/.test(ua)) {
    device = 'Windows';
  } else if (/Mac/.test(ua)) {
    device = 'Mac';
  } else if (/Linux/.test(ua)) {
    device = 'Linux';
  }

  // Detect browser
  if (/Chrome/.test(ua) && !/Edge|Edg/.test(ua)) {
    browser = 'Chrome';
  } else if (/Firefox/.test(ua)) {
    browser = 'Firefox';
  } else if (/Safari/.test(ua) && !/Chrome/.test(ua)) {
    browser = 'Safari';
  } else if (/Edge|Edg/.test(ua)) {
    browser = 'Edge';
  }

  return { device, browser, userAgent: ua.substring(0, 200) };
}

/**
 * Get FCM token and save to Firestore
 * @param {string} userId - The user's UID
 * @param {string} userRole - The user's role (learner, admin, specialist, etc.)
 */
export async function getFCMTokenAndSave(userId, userRole = 'learner') {
  if (!isNotificationSupported()) {
    console.warn('[FCM] Notifications not supported');
    return null;
  }

  if (Notification.permission !== 'granted') {
    console.warn('[FCM] Notification permission not granted');
    return null;
  }

  try {
    // Ensure service worker is registered
    // Register the FCM service worker if needed (the token must be tied to it)
    const registration =
      (await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js')) ||
      (await navigator.serviceWorker.register('/firebase-messaging-sw.js'));

    // Get messaging instance
    const messaging = getMessaging(app);

    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (!token) {
      console.warn('[FCM] No token received');
      return null;
    }

    console.log('[FCM] Token received:', token.substring(0, 20) + '...');

    // Save token to local storage
    localStorage.setItem(NOTIFICATION_TOKEN_KEY, token);

    // Get device info
    const deviceInfo = getDeviceInfo();

    // Create a unique token ID based on token hash
    const tokenId = await hashToken(token);

    // Guard against undefined path segments
    if (!userId || !tokenId || !token) {
      console.warn('[FCM] Missing userId or tokenId, skipping save');
      return null;
    }

    // Save to Firestore under user's fcmTokens subcollection
    const tokenRef = doc(db, 'users', userId, 'fcmTokens', tokenId);
    await setDoc(tokenRef, {
      token,
      device: deviceInfo.device,
      browser: deviceInfo.browser,
      userAgent: deviceInfo.userAgent,
      role: userRole,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      active: true
    });

    // Also save token reference at user level for easy querying
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      fcmToken: token,
      fcmTokenUpdatedAt: serverTimestamp(),
      notificationsEnabled: true
    }, { merge: true });

    console.log('[FCM] Token saved to Firestore');
    return token;
  } catch (error) {
    console.error('[FCM] Error getting/saving token:', error);
    return null;
  }
}

/**
 * Hash token for use as document ID
 */
async function hashToken(token) {
  const encoder = new TextEncoder();
  const data = encoder.encode(token);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').substring(0, 20);
}

/**
 * Remove FCM token from Firestore (e.g., on logout)
 * @param {string} userId - The user's UID
 */
export async function removeFCMToken(userId) {
  try {
    const token = localStorage.getItem(NOTIFICATION_TOKEN_KEY);
    if (!token) return;

    const tokenId = await hashToken(token);
    const tokenRef = doc(db, 'users', userId, 'fcmTokens', tokenId);
    await deleteDoc(tokenRef);

    // Update user document
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      notificationsEnabled: false,
      fcmTokenUpdatedAt: serverTimestamp()
    }, { merge: true });

    localStorage.removeItem(NOTIFICATION_TOKEN_KEY);
    console.log('[FCM] Token removed');
  } catch (error) {
    console.error('[FCM] Error removing token:', error);
  }
}

/**
 * Handle token refresh
 */
export function setupTokenRefresh(userId, userRole) {
  if (!isNotificationSupported()) return;

  try {
    // Listen for token refresh
    // Note: Firebase v9+ handles this differently - tokens are refreshed automatically
    // But we should re-save periodically
    setInterval(async () => {
      if (Notification.permission === 'granted') {
        await getFCMTokenAndSave(userId, userRole);
      }
    }, 24 * 60 * 60 * 1000); // Refresh daily
  } catch (error) {
    console.error('[FCM] Error setting up token refresh:', error);
  }
}

/**
 * Short two-tone chime, generated so no audio file is needed.
 */
function playAlertChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1175].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.18, ctx.currentTime + i * 0.22);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.22 + 0.2);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.22);
      o.stop(ctx.currentTime + i * 0.22 + 0.22);
    });
  } catch { /* sound is a nicety */ }
}

/**
 * Dispatch a top-of-page banner event for FCM foreground messages.
 * App.js listens for "fcm-banner" CustomEvent and renders an in-app banner.
 */
function dispatchFCMBanner(title, body, link) {
  window.dispatchEvent(new CustomEvent('fcm-banner', {
    detail: { title, body, link }
  }));
}

/**
 * Setup foreground message handler.
 * Routes FCM messages to the notification bell (Firestore) and an in-app
 * top-of-page banner — never to browser OS popups.
 *
 * @param {string}   userId           - Firebase UID (required to write bell notification)
 * @param {function} onMessageCallback - Optional extra callback
 */
export function setupForegroundMessages(userId, onMessageCallback) {
  if (!isNotificationSupported()) return null;

  try {
    const messaging = getMessaging(app);

    return onMessage(messaging, (payload) => {
      console.log('[FCM] Foreground message received:', payload);

      const title = payload.notification?.title || payload.data?.title || 'TheScope180';
      const body  = payload.notification?.body  || payload.data?.body  || 'You have a new notification';
      const link  = payload.data?.link || null;
      const type  = payload.data?.type || 'system';
      const icon  = payload.data?.icon || 'Info';

      // 1. Write to notification bell (persists for user to reference later)
      if (userId) {
        notifyBell(userId, { title, message: body, type, icon, link }).catch(() => {});
      }

      // 2. Show an in-app top-of-page banner (NOT a browser OS popup)
      dispatchFCMBanner(title, body, link);

      // 2b. Support alerts (chat, human requested, visitor) chime while the app is
      // open. The Support Queue page has its own sounds, and HumanRequestAlert
      // already chimes for human requests, so those are skipped.
      if (payload.data?.sound === '1' && payload.data?.type !== 'urgent' && !window.location.pathname.endsWith('/live-support')) {
        playAlertChime();
      }

      // 3. Optional caller-supplied callback
      if (onMessageCallback) {
        onMessageCallback(payload);
      }
    });
  } catch (error) {
    console.error('[FCM] Error setting up foreground messages:', error);
    return null;
  }
}

/**
 * Initialize notifications for a user
 * Call this after user logs in and performs a meaningful action
 */
export async function initializeNotifications(userId, userRole, options = {}) {
  const {
    autoRequest = false,
    onPermissionGranted = null,
    onPermissionDenied = null,
    onMessage = null
  } = options;

  // Check if already set up
  if (Notification.permission === 'granted') {
    const token = await getFCMTokenAndSave(userId, userRole);
    setupTokenRefresh(userId, userRole);
    setupForegroundMessages(userId, onMessage);
    if (onPermissionGranted) onPermissionGranted(token);
    return { permission: 'granted', token };
  }

  // Auto-request if specified
  if (autoRequest && Notification.permission === 'default') {
    const permission = await requestNotificationPermission();

    if (permission === 'granted') {
      const token = await getFCMTokenAndSave(userId, userRole);
      setupTokenRefresh(userId, userRole);
      setupForegroundMessages(userId, onMessage);
      if (onPermissionGranted) onPermissionGranted(token);
      return { permission, token };
    } else {
      if (onPermissionDenied) onPermissionDenied();
      return { permission, token: null };
    }
  }

  return { permission: Notification.permission, token: null };
}

const notificationPermission = {
  isNotificationSupported,
  getNotificationPermission,
  hasAskedForPermission,
  requestNotificationPermission,
  getFCMTokenAndSave,
  removeFCMToken,
  setupTokenRefresh,
  setupForegroundMessages,
  initializeNotifications
};

export default notificationPermission;
